import { useEffect, useState } from "react";
import { getGoogleTranslateLang } from "../../../utilities/googletranslate";
import { X } from "lucide-react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import JoditEditor from "jodit-react";
import { useRef } from "react";

export function ProfileCard({ currentUser, card, baseUrl }) {
    const editor = useRef(null);
    const client = useSelector((state) => state.client.client);
    const client_uid = client.uid || "clnt_QI9Jd1m0"; // fallback to default if not available
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    // For preview only
    const [imagePreview, setImagePreview] = useState(null);

    const [lang, setLang] = useState(getGoogleTranslateLang() || "norwegian");
    const [skillsOptions, setSkillsOptions] = useState([]);

    useEffect(() => {
        // run on mount
        setLang(getGoogleTranslateLang() || "norwegian");

        // optionally poll every second (Google widget does not fire events)
        const interval = setInterval(() => {
            const current = getGoogleTranslateLang();
            if (current && current !== lang) {
                setLang(current);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [lang]);

    useEffect(() => {
        const fetchAgent = async () => {
            try {
                const res = await axios.get(baseUrl + "/getAgentByUID", {
                    params: { token: currentUser.token, client_uid: 'clnt_QI9Jd1m0' },
                });
                console.log(res.data.data);
                // backend gives available skills (all options)
                const availableSkills = res.data.data?.skills_list || [];
                // example: [{skill_Id, name: hillfiger},{skill_Id: 2,name: Tarot}]

                // backend gives already selected skills for agent
                const currentSkills = res.data.data?.skills || [];

                var agent_pic_path = baseUrl.includes("localhost") ? "https://localhost/chamcore/chameleon/uploads/agent/" : "https://chamcore.com/chameleon/uploads/agent/";
                res.data.data.display_pic = agent_pic_path + res.data.data.display_pic;
                var bio = res.data.data.description === "" ? '' : JSON.parse(res.data.data.description);
                bio = bio[lang];
                setFormData({
                    name: res.data.data?.name || "",
                    skill_Id: currentSkills,
                    description: { [lang]: bio },
                    chatPrice: 29,
                    callPrice: res.data.data.customer_minute_price,
                    videoPrice: res.data.data.customer_minute_price,
                    image: res.data.data.display_pic,
                    country_code_ivr: res.data.data.phone_code,
                    ivr_number: res.data.data.company_no,
                    phonebill_code: res.data.data.phonebill_code,
                    phonebill: res.data.data.phonebill

                }) // save backend response to state

                // setImagePrview of agent display_pic
                setImagePreview(res.data.data.display_pic);
                setSkillsOptions(availableSkills); // store dropdown options
            } catch (err) {
                console.error("Error fetching agent:", err);
            } finally {
                setLoading(false);

            }
        };

        fetchAgent();
    }, [currentUser]);

    const options = skillsOptions.map((s) => ({
        value: s.skill_Id,
        label: s.name,
    }));

    // Current selected values for this language
    const selectedValues = options.filter((opt) =>
        (formData?.skill_Id || []).includes(opt.value)
    );

    // Handle image upload
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, image: file }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        setFormData((prev) => ({ ...prev, image: null }));
        setImagePreview(null);
    };

    // Handle text/number fields
    const handleChange = (eOrValue) => {
        // if it's a native event
        if (eOrValue?.target) {
            const { name, value } = eOrValue.target;
            if (name.startsWith("description")) {
                setFormData((prev) => ({
                    ...prev,
                    description: {
                        ...prev.description,
                        [lang]: value,
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    [name]: value,
                }));
            }
        } else {
            // assume it's the HTML string from JoditEditor
            setFormData((prev) => ({
                ...prev,
                description: {
                    ...prev.description,
                    [lang]: eOrValue,
                },
            }));
        }
    };

    // Handle form submit with axios
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // If sending image, use FormData
            const data = new FormData();
            // Normal fields
            data.append("name", formData.name);
            data.append("skill_Id", formData.skill_Id);
            data.append("chatPrice", formData.chatPrice);
            data.append("callPrice", formData.callPrice);
            data.append("videoPrice", formData.videoPrice);

            // ✅ If File, FormData will handle file upload automatically
            if (formData.image) {
                data.append("image", formData.image);
            }

            // Multi-language description
            Object.entries(formData.description).forEach(([lang, bio]) => {
                data.append(`description[${lang}]`, bio);
            });

            // Token
            data.append("token", currentUser.token);

            const res = await axios.post(baseUrl + "/updateAgentProfile", data, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            console.log("Profile saved:", res.data);
            Swal.fire({
                icon: "success",
                title: "Profil oppdatert!",
                text: "Dine endringer er lagret.",
                showConfirmButton: false,
                timer: 2000,
            });
        } catch (err) {
            console.error("Error saving profile:", err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-600 animate-pulse">Laster inn profil…</p>
                </div>
            </div>
        );
    }
    return (
        <form onSubmit={handleSubmit} className={card}>
            <div className="grid gap-4 md:grid-cols-2">
                {/* Left: Profile Info */}
                <div>
                    <h3 className="font-bold text-purple-900 mb-2">Profil</h3>

                    <label className="text-sm text-gray-700">Visningsnavn</label>
                    <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full rounded-xl border px-3 py-2 mb-3"
                    />

                    <label className="text-sm text-gray-700">Tagline</label>
                    <Select
                        isMulti
                        name={`skill_Id`}
                        options={options}
                        value={selectedValues}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="Choose skills..."
                        onChange={(selected) => {
                            const selectedIds = selected.map((s) => s.value);
                            setFormData((prev) => ({
                                ...prev,
                                skill_Id: selectedIds,
                            }));
                        }}
                    />

                    <label className="text-sm text-gray-700">Bio</label>
                    <JoditEditor
                        name={`description[${lang}]`}
                        ref={editor}
                        value={formData.description[lang]}
                        onChange={handleChange}
                        config={{
                            askBeforePasteHTML: true,
                            askBeforePasteFromWord: false,
                            i18n: {
                                en: {
                                    'Keep': 'Keep Formatting',
                                    'Insert as Text': 'Paste Plain Text',
                                    'Cancel': 'Close'
                                }
                            }
                        }}
                    />

                </div>

                {/* Right: Prices */}
                <div>
                    <h3 className="font-bold text-purple-900 mb-2">Priser</h3>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                            <label>Chat / min</label>
                            <input
                                type="number"
                                name="chatPrice"
                                value={formData.chatPrice}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                        <div>
                            <label>Samtale / min</label>
                            <input
                                type="number"
                                name="callPrice"
                                value={formData.callPrice}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                        <div>
                            <label>Video / min</label>
                            <input
                                type="number"
                                name="videoPrice"
                                value={formData.videoPrice}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-sm">
                        <div>
                            <label>Country code</label>
                            <input
                                type="number"
                                name="chatPrice"
                                value={formData.country_code_ivr}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                        <div>
                            <label>IVR number</label>
                            <input
                                type="number"
                                name="callPrice"
                                value={formData.ivr_number}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                        <div>
                            <label>Country code</label>
                            <input
                                type="number"
                                name="videoPrice"
                                value={formData.phonebill_code}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                        <div>
                            <label>Phonebill</label>
                            <input
                                type="number"
                                name="videoPrice"
                                value={formData.phonebill}
                                onChange={handleChange}
                                className="w-full rounded-xl border px-2 py-1"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Prisene kan vises offentlig i din profil.
                    </p>
                    <h3 className="font-bold text-purple-900 my-2">Profilbilde</h3>
                    {/* Profile Image Upload */}
                    <label className="text-sm text-gray-700">Profilbilde</label>
                    <div className="flex items-center gap-4 my-3">
                        {imagePreview ? (
                            <div className="relative">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-20 h-20 rounded-full object-cover border"
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="absolute -top-2 -right-2 rounded-full bg-red-600 text-white p-1 shadow hover:bg-red-700"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <div className="w-20 h-20 rounded-full border flex items-center justify-center text-gray-400 text-sm">
                                Ingen
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Submit Button */}
            <div className="mt-4">
                <button
                    type="submit"
                    className="bg-purple-700 text-white px-4 py-2 rounded-lg hover:bg-purple-800"
                >
                    Lagre profil
                </button>
            </div>
        </form>
    );
}
